import type { ReactElement } from 'react';

import {
  Alert,
  Badge,
  Box,
  Container,
  ExpandableSection,
  Header,
  Link,
  SpaceBetween,
  StatusIndicator,
  Table,
} from '@cloudscape-design/components';

import type {
  DriftFinding,
  DriftReport,
  RealmDetail,
  RealmRow,
} from '../../api/ztrust';

interface RealmListProps {
  isLoading: boolean;
  realms: RealmRow[];
  selectedRealm: string | undefined;
  onSelect: (realm: string | undefined) => void;
}

export function RealmList({
  isLoading,
  realms,
  selectedRealm,
  onSelect,
}: RealmListProps): ReactElement {
  return (
    <Table
      variant="embedded"
      loading={isLoading}
      loadingText="Reading realms"
      items={realms}
      selectionType="single"
      selectedItems={realms.filter((realm) => realm.realm === selectedRealm)}
      onSelectionChange={({ detail }) => onSelect(detail.selectedItems[0]?.realm)}
      trackBy="realm"
      empty={<Box textAlign="center">No realms returned.</Box>}
      columnDefinitions={[
        {
          id: 'realm',
          header: 'Realm',
          cell: (realm) => (
            <Link
              href="#"
              onFollow={(event) => {
                event.preventDefault();
                onSelect(realm.realm);
              }}
            >
              {realm.realm}
            </Link>
          ),
        },
        { id: 'name', header: 'Display name', cell: (realm) => realm.displayName ?? '—' },
        {
          id: 'enabled',
          header: 'Enabled',
          cell: (realm) => (
            <StatusIndicator type={realm.enabled ? 'success' : 'stopped'}>
              {realm.enabled ? 'enabled' : 'disabled'}
            </StatusIndicator>
          ),
        },
        {
          id: 'idp',
          header: 'Identity providers',
          cell: (realm) =>
            realm.identityProviders.length > 0 ? (
              realm.identityProviders.join(', ')
            ) : (
              <StatusIndicator type="warning">none</StatusIndicator>
            ),
        },
        { id: 'groups', header: 'Groups', cell: (realm) => realm.groupCount },
        {
          id: 'hardening',
          header: 'Hardening',
          cell: (realm) => (
            <StatusIndicator
              type={realm.hardening.failing.length === 0 ? 'success' : 'warning'}
            >
              {realm.hardening.passed}/{realm.hardening.total}
            </StatusIndicator>
          ),
        },
      ]}
    />
  );
}

interface DriftListProps {
  isLoading: boolean;
  report: DriftReport | undefined;
}

export function DriftList({ isLoading, report }: DriftListProps): ReactElement {
  const findings = report?.findings ?? [];
  return (
    <SpaceBetween size="m">
      <Alert type="info" header="Declarative source">
        {report
          ? `${report.source.repository}/${report.source.path} — isolated state ${report.source.stateKey}`
          : 'Loading the reviewed Terraform projection.'}
        {' '}Terraform remains the only configuration writer.
      </Alert>
      <Table
        variant="embedded"
        loading={isLoading}
        items={findings}
        empty={<Box textAlign="center">No divergence found.</Box>}
        columnDefinitions={[
          {
            id: 'severity',
            header: 'Severity',
            cell: (finding: DriftFinding) => (
              <Badge
                color={
                  finding.severity === 'high'
                    ? 'red'
                    : finding.severity === 'medium'
                      ? 'blue'
                      : 'grey'
                }
              >
                {finding.severity}
              </Badge>
            ),
          },
          { id: 'realm', header: 'Realm', cell: (finding) => finding.realm },
          { id: 'subject', header: 'Subject', cell: (finding) => finding.subject },
          { id: 'finding', header: 'Finding', cell: (finding) => finding.finding },
        ]}
      />
    </SpaceBetween>
  );
}

export function RealmDetails({
  detail,
}: {
  detail: RealmDetail | undefined;
}): ReactElement {
  if (!detail) {
    return <Box textAlign="center" padding="l">Select a realm.</Box>;
  }
  return (
    <SpaceBetween size="l">
      <HardeningTable detail={detail} />
      <ClientTable detail={detail} />
      <GroupTable detail={detail} />
    </SpaceBetween>
  );
}

function HardeningTable({ detail }: { detail: RealmDetail }): ReactElement {
  return (
    <Container header={<Header variant="h3">Hardening</Header>}>
      <Table
        variant="embedded"
        items={detail.hardening}
        trackBy="id"
        columnDefinitions={[
          {
            id: 'pass',
            header: 'Result',
            cell: (check) => (
              <StatusIndicator type={check.pass ? 'success' : 'error'}>
                {check.pass ? 'pass' : 'fail'}
              </StatusIndicator>
            ),
          },
          { id: 'requirement', header: 'Requirement', cell: (check) => check.requirement },
          { id: 'detail', header: 'Live value', cell: (check) => check.detail },
        ]}
      />
    </Container>
  );
}

function ClientTable({ detail }: { detail: RealmDetail }): ReactElement {
  return (
    <Container header={<Header variant="h3" counter={`(${detail.clients.length})`}>Clients</Header>}>
      <Table
        variant="embedded"
        items={detail.clients}
        trackBy="clientId"
        columnDefinitions={[
          { id: 'clientId', header: 'Client', cell: (client) => client.clientId },
          {
            id: 'type',
            header: 'Type',
            cell: (client) => (
              <Badge color={client.publicClient ? 'blue' : 'green'}>
                {client.publicClient ? 'public' : 'confidential'}
              </Badge>
            ),
          },
          {
            id: 'grants',
            header: 'Risk flags',
            cell: (client) => (
              <SpaceBetween size="xxs" direction="horizontal">
                {client.implicitFlowEnabled && <Badge color="red">implicit</Badge>}
                {client.directAccessGrantsEnabled && <Badge color="red">password</Badge>}
                {!client.implicitFlowEnabled && !client.directAccessGrantsEnabled && <Badge color="green">none</Badge>}
              </SpaceBetween>
            ),
          },
          {
            id: 'redirects',
            header: 'Redirect URIs',
            cell: (client) =>
              client.redirectUris.length === 0 ? (
                '—'
              ) : (
                <ExpandableSection headerText={`${client.redirectUris.length}`} variant="footer">
                  <Box variant="code">{client.redirectUris.join('\n')}</Box>
                </ExpandableSection>
              ),
          },
        ]}
      />
    </Container>
  );
}

function GroupTable({ detail }: { detail: RealmDetail }): ReactElement {
  return (
    <Container header={<Header variant="h3" counter={`(${detail.groups.length})`}>Groups and precedence</Header>}>
      <Table
        variant="embedded"
        items={detail.groups}
        trackBy="path"
        empty={<Box textAlign="center">No groups. Brokered identities have no local entitlement source.</Box>}
        columnDefinitions={[
          { id: 'path', header: 'Path', cell: (group) => group.path },
          {
            id: 'precedence',
            header: 'Precedence',
            cell: (group) => group.precedenceRaw ?? 'omit',
          },
          {
            id: 'primary',
            header: 'Primary eligible',
            cell: (group) => (group.neverPrimary ? 'no' : 'yes'),
          },
        ]}
      />
    </Container>
  );
}
